import { getIn } from "final-form";
import { useEffect, useState } from "react";
import { useForm } from "react-final-form";
import styles from './DebtScheduleGrid.module.scss';
import MeritoButton from "../../../../components/common/MeritoButtons/MeritoButtons";
import ShortcutModal from "../../components/ReactGrid/ShortcutModal";
import { shortcutType } from "../../components/ReactGrid/reactGridHelper";
import _ from 'lodash';
import { debtScheduleHeaders, debtScheduleHeadersObj, getDebtScheduleGridInvalidRange, getInitialDebtSchedule, onAddInitialDebtScheduleRow } from './debtScheduleHelper'
import { NonEditableCell, ReactGrid, TextCell } from "@silevis/reactgrid";
import { CustomNumberCell } from "../../components/ReactGrid/CustomNumberCell";
import { revenueGridStyles } from "../revenueGridStyles";
import { CustomTextCell } from "../../components/ReactGrid/CustomTextCell";
import { DateCell } from "../../components/ReactGrid/CustomDateCell";
import { Modal } from "reactstrap";
import { BsFillExclamationCircleFill as WarningIcon } from 'react-icons/bs'
import { ApplicationContextType, underwritingFormWarningType } from "../../../../interfaces/interfaces";
import { RiAddFill as AddIcon } from 'react-icons/ri'
import { shareholderHeaders } from "../../CompanyDetails/Shareholder/shareholderHelper";
import { getLastDayOfPreviousMonthFormatted } from "../DebtorSchedule/DebtorScheduleGrid";
import { CustomSelectCell } from "../../components/ReactGrid/CustomSelectCell";

type Props = {
  fieldName: string;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  formType: ApplicationContextType;
}

const getRows = (gridData) => {
  if (!gridData?.rowData?.length) return [];

  const headerRow = {
    rowIndex: 0,
    height: 42, // Explicitly setting height for headers
  };

  const dataRows = gridData.rowData.map((_, rowIndex) => ({
    rowIndex: rowIndex + 1, // Adjusting index for data rows
    height: 42,
  }));

  const footerRow = {
    rowIndex: gridData.rowData.length + 1, // Footer comes after last data row
    height: 42,
  };

  return [headerRow, ...dataRows, footerRow]; // Include header, data, and footer rows
};


const generateCells = (gridData, updateCellValue, addRow, deleteRow) => {
  // console.log('gridData', gridData);
  if (!gridData?.rowData?.length) return [];
  const headerCells = debtScheduleHeaders.map((header, colIndex) => {
    const headersObj = debtScheduleHeadersObj[header];
    return {
      rowIndex: 0,
      colIndex,
      isSelectable: false,
      isFocusable: false,
      Template: NonEditableCell,
      props: {
        value: `${headersObj?.title}${headersObj?.isMandatory ? '*' : ''}`,
        style: {
          backgroundColor: "#F9F9F9",
          paddingTop: "0.75rem",
          paddingLeft: "0.5rem",
          paddingRight: "0.5rem",
          paddingBottom: "0.75rem",
          ...headersObj.styles,
        }
      },
    }
  });
  const dataCells = gridData.rowData.flatMap((row, rowIndex) => {
    return debtScheduleHeaders.map((headerKey, colIndex) => {
      const headersObj = debtScheduleHeadersObj[headerKey];


      const cellValue = row[headerKey];
      const allowShortcuts = gridData.rowData.length > 1 ? [shortcutType.addRow, shortcutType.deleteRow] : [shortcutType.addRow];

      if (headersObj?.type == "string") {
        return {
          rowIndex: rowIndex + 1,
          colIndex,
          Template: CustomTextCell,
          props: {
            text: cellValue,
            onTextChanged: (newText) => updateCellValue(rowIndex, headerKey, newText),
            onAddRow: (newRowIndex) => addRow(newRowIndex),
            onDeleteRow: (newRowIndex) => deleteRow(newRowIndex),
            allowShortcuts: allowShortcuts,
            style: {
              paddingTop: "0.75rem",
              paddingLeft: "0.5rem",
              paddingRight: "0.5rem",
              paddingBottom: "0.75rem",
              ...headersObj.styles,
            }
          },
        }
      } else if (headersObj?.type == "number") {
        return {
          rowIndex: rowIndex + 1,
          colIndex,
          Template: CustomNumberCell,
          props: {
            value: cellValue,
            onValueChanged: (newValue) => updateCellValue(rowIndex, headerKey, newValue),
            onAddRow: (newRowIndex) => addRow(newRowIndex),
            onDeleteRow: (newRowIndex) => deleteRow(newRowIndex),
            allowShortcuts: allowShortcuts,
            format: headersObj?.format || null,
            hideZero: headersObj?.hideZero,
            style: {
              paddingTop: "0.75rem",
              paddingLeft: "0.5rem",
              paddingRight: "0.5rem",
              paddingBottom: "0.75rem",
              ...headersObj.styles,
            }
          },
        }
      } else if (headersObj?.type == "date") {
        return {
          rowIndex: rowIndex + 1,
          colIndex,
          Template: DateCell,
          props: {
            value: cellValue,
            onValueChanged: (newValue) => updateCellValue(rowIndex, headerKey, newValue),
            onAddRow: (newRowIndex) => addRow(newRowIndex),
            onDeleteRow: (newRowIndex) => deleteRow(newRowIndex),
            allowShortcuts: allowShortcuts,
            style: {
              paddingTop: "0.75rem",
              paddingLeft: "0.5rem",
              paddingRight: "0.5rem",
              paddingBottom: "0.75rem",
              ...headersObj.styles,
            }
          },
        }
      } else if (headersObj?.type == "select") {
        return {
          rowIndex: rowIndex + 1,
          colIndex,
          Template: CustomSelectCell,
          props: {
            value: cellValue,
            onValueChanged: (newValue) => updateCellValue(rowIndex, headerKey, newValue),
            onAddRow: (newRowIndex) => addRow(newRowIndex),
            onDeleteRow: (newRowIndex) => deleteRow(newRowIndex),
            options: headersObj?.options,
            optionLabelKey: headersObj?.optionLabelKey,
            allowShortcuts: allowShortcuts,
            style: {
              paddingTop: "0.75rem",
              paddingLeft: "0.5rem",
              paddingRight: "0.5rem",
              paddingBottom: "0.75rem",
              ...headersObj.styles,
            }
          }
        }
      }
    })
  });

  const footerCells = debtScheduleHeaders.map((headerKey, colIndex) => {
    const cellValue = gridData.footer[headerKey] // Use footer data or default to "-"
    const headersObj = debtScheduleHeadersObj[headerKey];

    // Format the value if it's a number and has a format specified
    let displayValue = cellValue;
    if (headersObj?.format && typeof cellValue === 'number') {
      displayValue = headersObj.format.format(cellValue);
    }

    // console.log('headersObj', headersObj);
    return {
      rowIndex: gridData.rowData.length + 1,
      colIndex,
      isSelectable: true,
      isFocusable: true,
      Template: NonEditableCell,
      props: {
        value: displayValue,
        style: {
          backgroundColor: "#F9F9F9",
          paddingTop: "0.75rem",
          paddingLeft: "0.5rem",
          paddingRight: "0.5rem",
          paddingBottom: "0.75rem",
          fontWeight: "600",
          ...headersObj.styles,
        }
      },
    }
  });

  // console.log('dataCells', dataCells);
  // console.log('footerCells', footerCells);

  return [...headerCells, ...dataCells, ...footerCells];
}


const DebtScheduleGrid = ({ fieldName, isOpen, setIsOpen, formType }: Props) => {
  const { getState, change } = useForm();

  const debtSchedule = getIn(getState()?.values, `${fieldName}`);

  const [gridData, setGridData] = useState({
    headers: [],
    rowData: [],
    footer: {}
  });

  const [validation, setValidation] = useState({
    message: '',
    styledRange: []
  });

  const [initialGridData, setInitialGridData] = useState(null);
  const [isShorcutModalOpen, setIsShorcutModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [warning, setWarning] = useState({
    state: false,
    type: null
  });


  useEffect(() => {
    if (isOpen) {
      if (!debtSchedule) {
        const debtScheduleGridData = getInitialDebtSchedule();
        setGridData(debtScheduleGridData);
        setInitialGridData(_.cloneDeep({
          headers: debtScheduleGridData.headers,
          rowData: debtScheduleGridData.rowData,
          footer: debtScheduleGridData.footer
        }))
      } else {
        setInitialGridData(_.cloneDeep(debtSchedule));
        setGridData(debtSchedule);
      }
    }
  }, [isOpen]);

  const updateCellValue = (rowIndex: string, columnKey: string, value) => {
    setGridData((prev) => {
      // console.log('path', path, 'value', value);
      const newGridData = _.cloneDeep(prev?.rowData);
      _.set(newGridData, `${rowIndex}.${columnKey}`, value);

      const footerData = _.cloneDeep(prev?.footer);

      // const [rowIndex, colIndex] = path.match(/\d+/g).map(Number);
      // calculate row sum 
      const headersObj = debtScheduleHeadersObj[columnKey];
      if (headersObj?.isTotal) {
        const sum = newGridData.reduce((acc, row) => {
          return acc + (row[columnKey] || 0);
        }, 0);
        // newGridData[newGridData.length - 1][colIndex] = sum;
        if (newGridData.length == 1 && !sum) footerData[columnKey] = "-";
        else footerData[columnKey] = sum;
      }
      return {
        ...prev,
        rowData: newGridData,
        footer: footerData,
      };
    })
  }

  const addRow = (rowIndex: number) => {
    setGridData((prev) => {
      const newGridData = _.cloneDeep(prev?.rowData);
      newGridData.splice(rowIndex, 0, onAddInitialDebtScheduleRow());
      return {
        ...prev,
        rowData: newGridData,
      };
    })
  }

  const deleteRow = (rowIndex: number) => {
    // console.log('rowIndex', rowIndex);
    setGridData((prev) => {
      const newGridData = _.cloneDeep(prev?.rowData);
      newGridData.splice(rowIndex, 1);
      // calculate footer sum

      const footerData = {};
      debtScheduleHeaders.forEach((headerKey, colIndex) => {
        footerData[headerKey] = 0;
      });


      // const footerData = new Array(prev.headers.length).fill(0);
      newGridData.forEach((row, rowIndex) => {
        debtScheduleHeaders.forEach((headerKey, colIndex) => {
          const cellValue = row[headerKey];
          const headersObj = debtScheduleHeadersObj[headerKey];
          if (headersObj?.isTotal) {
            const sumValue = (footerData[headerKey] || 0) + (cellValue || 0);
            if (newGridData?.length == 1 && !sumValue) footerData[headerKey] = "-";
            else footerData[headerKey] = sumValue;
          } else {
            if (colIndex == 0) footerData[headerKey] = "Total";
            else footerData[headerKey] = "-";
          }
        })
      });


      return {
        ...prev,
        rowData: newGridData,
        footer: footerData,
      }
    });
  }


  const columns = debtScheduleHeaders.map((_, colIndex) => ({
    colIndex,
    width: 180,
  }));

  const rows = getRows(gridData);
  const cells = generateCells(gridData, updateCellValue, addRow, deleteRow);

  const validateGrid = () => {
    const { missingDataErrors, dateErrors } = getDebtScheduleGridInvalidRange(gridData);

    if (missingDataErrors.length) {
      setValidation({
        message: "Some cells are missing data.",
        styledRange: missingDataErrors
      });
    } else if (dateErrors.length) {
      setValidation({
        message: "Some date fields contain invalid values.",
        styledRange: dateErrors
      });
    } else {
      setValidation({ message: "", styledRange: [] });
    }

    return missingDataErrors.length === 0 && dateErrors.length === 0; // Returns true if grid is valid
  }

  return (
    <div className={styles.rootContainer}>
      <div className={styles.headerContainer}>
        <div className={styles.leftContainer}>
          <div className={styles.title}>
            Debt Schedule (As of {getLastDayOfPreviousMonthFormatted()})
          </div>
        </div>
        <div className={styles.rightContainer}>
          <MeritoButton
            btnText="Save"
            type='primary-1'
            onClick={() => {
              // save the provisional statement
              const isValid = validateGrid();
              setIsSubmitted(true);
              if (isValid) {
                change(fieldName, gridData);
                setIsOpen(false);
              } else {
                setWarning({
                  state: true,
                  type: underwritingFormWarningType.save
                })
              }
            }}
          />
          <MeritoButton
            btnText="Cancel"
            type='secondary-1'
            onClick={() => {
              // setIsOpen(false)
              const stringifiedPrevData = JSON.stringify(initialGridData);
              const stringifiedNewData = JSON.stringify(gridData);
              if (stringifiedPrevData !== stringifiedNewData) {
                setWarning({
                  state: true,
                  type: underwritingFormWarningType.cancel
                });
              } else {
                setIsOpen(false)
              }
            }}
          />
        </div>
      </div>
      <div className={styles.tipsContainer}>
        <div className={styles.tipsTitle}>
          Tips:
        </div>
        <div className={styles.tipsContent}>
          <ol>
            {formType == ApplicationContextType.monthlyReport ? <li>Update existing loan amounts. Add new loans.</li> : null}
            <li>In the Escrow Held column, enter the name of the marketplace, courier, or payment gateway if an escrow has been created over its receivables.</li>
            <li>You can use these <span onClick={() => {
              setIsShorcutModalOpen(true)
            }}>spreadsheet shortcuts.</span></li>
            <li>Fields marked with an asterisk (*) are mandatory</li>
          </ol>
        </div>
      </div>
      <div className={styles.tableRootContainer}>
        {gridData?.rowData?.length ? <ReactGrid
          id="debtScheduleGrid"
          columns={columns}
          rows={rows}
          cells={cells}
          stickyTopRows={1}
          stickyBottomRows={1}
          styledRanges={validation.styledRange}
          styles={revenueGridStyles?.rgStyles}
        /> : null}
      </div>

      {validation.message ? <div className={styles.errorMessage}>
        {validation.message}
      </div> : null}
      <div className={styles.btnContainer}>
        <MeritoButton
          type="secondary-1"
          size="md"
          icon={<AddIcon />}
          btnText="Insert Row"
          onClick={() => {
            addRow(gridData.rowData.length);
          }}
        />
      </div>

      <Modal
        size={"md"}
        isOpen={warning?.state}
        toggle={() => {
          setWarning({ state: false, type: null });
        }}
        centered={true}
        style={{ width: '450px' }}
        contentClassName='shortcut_modal'
      >
        <div className={styles.confirmationRootContainer}>
          <div className={styles.topContainer}>
            <div className={styles.iconContainer}>
              <WarningIcon />
            </div>
            <div className={styles.messageContainer}>
              {warning?.type === underwritingFormWarningType.save ? 'Required Details Are Missing!' : 'Unsaved Changes Will Be Lost!'}
            </div>
            {warning?.type == underwritingFormWarningType.save ? <div className={styles.subMessageContainer}>
              Do you want to proceed anyway?
            </div> : null}
          </div>

          <div className={styles.bottomContainer}>
            {warning?.type === underwritingFormWarningType.save ?
              <>
                <MeritoButton
                  btnText={'Save & Continue'}
                  size='lg'
                  isRounded={true}
                  type='secondary-2'
                  onClick={() => {
                    change(fieldName, gridData);
                    setWarning({
                      state: false,
                      type: null
                    })
                    setIsOpen(false);
                  }}
                />
                <MeritoButton
                  btnText="Continue Editing"
                  size='lg'
                  isRounded={true}
                  type='primary-1'
                  onClick={() => {
                    setWarning({
                      state: false,
                      type: null
                    });
                  }}
                />
              </>
              :
              <>
                <MeritoButton
                  btnText={'Delete Changes'}
                  size='lg'
                  isRounded={true}
                  type='secondary-2'
                  onClick={() => {
                    setWarning({
                      state: false,
                      type: null
                    })
                    setIsOpen(false);
                  }}
                />
                <MeritoButton
                  btnText="Continue Editing"
                  size='lg'
                  isRounded={true}
                  type='primary-1'
                  onClick={() => {
                    setWarning({
                      state: false,
                      type: null
                    });
                  }}
                />
              </>
            }

          </div>
        </div>
      </Modal>

      <ShortcutModal isOpen={isShorcutModalOpen} setIsOpen={setIsShorcutModalOpen}
        showShortcuts={[shortcutType.addRow, shortcutType.deleteRow, shortcutType.cut, shortcutType.copy, shortcutType.paste, shortcutType.moveLeft, shortcutType.moveRight]} />
    </div>
  );
}

export default DebtScheduleGrid;